import classNames from "classnames";
import ButtonLike from "components/partial/button-like/button-like";
import StarRating from "components/partial/star-rating/star-rating";
import { ReactComponent as ClockIcon } from "assets/icons/ic_clock.svg";
import { ReactComponent as EyeOnIcon } from "assets/icons/ic_eye-on.svg";
import { IPreviewGroupModel } from "models/dto/ZoomiLxp/Models/GroupModels/IPreviewGroupModel";
import { getPicture } from "helpers/image.helper";
import { formatCourseDuration } from "helpers/time.helper";
import { generatePath, NavLink, useHistory } from "react-router-dom";
import { Routes } from "routes";
import styles from "./styles.module.scss";

interface Props {
	groupInfo: IPreviewGroupModel;
	tileStyle?: "default" | "light";
	className?: string;
	openInNewTab?: boolean;
}

const getPrice = (groupPrice?: number) => {
	if (groupPrice) {
		const price = groupPrice / 100;
		return groupPrice % 100 === 0 ? `$${price}` : `$${price.toFixed(2)}`;
	}
	return "Free";
};

const GroupTile = ({ groupInfo, tileStyle = "default", className, openInNewTab }: Props) => {
	const isLight = tileStyle === "light";
	const price = getPrice(groupInfo.price);
	const history = useHistory();
	const groupPath = generatePath(Routes.GroupPage, { id: groupInfo.id });
	const goToGroup = () => {
		openInNewTab ? window.open(groupPath, "_blank", "noopener, noreferrer") : history.push(groupPath);
	};

	return (
		<div className={classNames(styles.group_tile, { [styles.group_tile__light]: isLight }, className)}>
			<div className={styles.group_tile__header}>
				<div className={styles.group_tile__duration_block}>
					<ClockIcon
						className={classNames(styles.group_tile__clock_icon, {
							[styles.group_tile__icon__dark]: isLight,
						})}
					/>
					<span className={styles.group_tile__duration}>{formatCourseDuration(groupInfo.durationMinutes)}</span>
				</div>
				<StarRating
					className={styles.group_tile__rating}
					value={groupInfo.rating}
					isDisabled={true}
					darkBorder={isLight}
				/>
			</div>
			<div className={styles.group_tile__content}>
				<img
					src={getPicture(groupInfo)}
					alt="Group thumbnail"
					className={styles.group_tile__thumbnail}
					onClick={goToGroup}
				/>
			</div>
			<div className={styles.group_tile__info_container}>
				<NavLink
					to={groupPath}
					className={classNames(styles.group_tile__title, { [styles.group_tile__title__dark]: isLight })}
					rel="noopener, noreferrer"
					target={openInNewTab ? "_blank" : "_self"}
					onClick={(event) => {
						if (openInNewTab) return;
						event.preventDefault();
						goToGroup();
					}}
				>
					{groupInfo.displayName}
				</NavLink>
				<div className={styles.group_tile__stats_section}>
					<div className={styles.group_tile__stats_left_block}>
						<div className={styles.group_tile__likes}>
							<ButtonLike
								isLikable={false}
								isActive={true}
								isSecondaryType={false}
								className={styles.group_tile__stats_icon}
							/>
							{groupInfo.likesCount}
						</div>
						<div className={styles.group_tile__views}>
							<EyeOnIcon
								className={classNames(styles.group_tile__stats_icon, styles.group_tile__eye_icon, {
									[styles.group_tile__eye_icon__dark]: isLight,
								})}
							/>
							{groupInfo.viewCount}
						</div>
					</div>
				</div>
				<div className={styles.group_tile__details_section}>
					<div className={styles.group_tile__info_box}>
						<p className={styles.group_tile__content_info}>
							<b>Courses: </b> {groupInfo.courseCount}
						</p>
						<p className={styles.group_tile__content_info}>
							<b>Contents: </b> {groupInfo.contentCount}
						</p>
					</div>
					{!groupInfo.isMember && (
						<div className={styles.group_tile__price_box}>
							<span className={styles.group_tile__price_title}>Price:</span>
							{price}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default GroupTile;
